import { Fragment, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams } from 'react-router-dom';

const SUBTYPE_LABEL = {
  ENGAGEMENT: 'Engagement', IG_BUSINESS: 'IG', LOOKALIKE: 'Lookalike',
  WEBSITE: 'Website', VIDEO: 'Video', CUSTOM: 'Custom',
};
const subtypeLabel = (s) => (s ? (SUBTYPE_LABEL[s] || s) : '—');
const fmtCount = (n) => {
  if (n == null) return '—';
  if (n >= 1e6) return `~${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `~${Math.round(n / 1e3)}K`;
  return `~${n}`;
};

export default function MetaAudiences() {
  const { tenantSlug } = useParams();
  const [data, setData] = useState({ audiences: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [sortKey, setSortKey] = useState('artist');
  const [sortDir, setSortDir] = useState('asc');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [pushTarget, setPushTarget] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const load = () => {
    setLoading(true);
    apiFetch(`/api/tenants/${tenantSlug}/meta-audiences`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.error);
        setData(d);
        setLoading(false);
      })
      .catch((e) => { setError(e.message); setLoading(false); });
  };
  useEffect(load, [tenantSlug]);

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/meta-audiences/sync`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) {
        setSyncResult({ ok: false, error: d.error, detail: d.detail });
      } else {
        setSyncResult({ ok: true, synced: d.synced });
        load();
      }
    } catch (e) {
      setSyncResult({ ok: false, error: e.message });
    } finally {
      setSyncing(false);
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    // Size defaults to desc (biggest first); everything else to asc.
    else { setSortKey(key); setSortDir(key === 'size' ? 'desc' : 'asc'); }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (error) return <div className="text-destructive">Error: {error}</div>;

  const audSize = (a) => a.approx_count_upper ?? a.approx_count_lower ?? -1;
  const sign = sortDir === 'asc' ? 1 : -1;
  const visible = data.audiences
    .filter((a) => showInactive || a.active)
    .filter((a) => !filter || a.artist.toLowerCase().includes(filter.toLowerCase()) || (a.audience_name || '').toLowerCase().includes(filter.toLowerCase()) || a.audience_id.includes(filter))
    .sort((a, b) => {
      switch (sortKey) {
        case 'subtype':     return sign * subtypeLabel(a.subtype).localeCompare(subtypeLabel(b.subtype));
        case 'size':        return sign * (audSize(a) - audSize(b));
        case 'audience_id': return sign * a.audience_id.localeCompare(b.audience_id, undefined, { numeric: true });
        case 'page':        return sign * (a.page?.name || '').localeCompare(b.page?.name || '');
        case 'status':      return sign * ((a.active ? 1 : 0) - (b.active ? 1 : 0));
        case 'artist':
        default:            return sign * a.artist.localeCompare(b.artist);
      }
    });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Meta audiences</h1>
        <div className="flex items-center gap-3">
          {syncResult && (
            <span className={`text-xs ${syncResult.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`} title={syncResult.detail || ''}>
              {syncResult.ok ? `synced ${syncResult.synced}` : `✗ ${syncResult.error}`}
            </span>
          )}
          <button
            onClick={() => setUploadOpen(true)}
            className="px-3 py-1 text-xs border border-primary text-primary rounded-md hover:bg-primary/10"
            title="Create a customer-file audience from CRM contacts (CSV or pasted list)"
          >⬆ Upload custom audience</button>
          <button
            onClick={runSync}
            disabled={syncing}
            className="px-3 py-1 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
            title="Pull latest audience list from Meta Marketing API"
          >{syncing ? 'Syncing…' : '↻ Sync from Meta'}</button>
          <div className="text-sm text-muted-foreground">
            {visible.length} of {data.count}
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-4 items-center">
        <input
          type="text"
          placeholder="Filter by artist or audience ID…"
          className="flex-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded"
          />
          Show inactive
        </label>
      </div>

      {/* Mobile: stacked cards (the table below is md+ only) */}
      <div className="md:hidden space-y-2">
        {visible.map((a) => {
          const isOpen = expanded.has(a.id);
          return (
          <div key={a.id} className="border border-border rounded-md p-3 bg-card text-sm">
            <div className="flex items-center justify-between gap-2 cursor-pointer" onClick={() => toggleExpand(a.id)}>
              <span className="font-medium truncate">{a.artist}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                {a.active ? (
                  <span className="text-xs text-green-700 dark:text-green-300">active</span>
                ) : (
                  <span className="text-xs text-muted-foreground">inactive</span>
                )}
                <span className="text-muted-foreground">{isOpen ? '▾' : '▸'}</span>
              </div>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {subtypeLabel(a.subtype)} · {fmtCount(a.approx_count_upper ?? a.approx_count_lower)}
              {a.page && <span> · {a.page.name}</span>}
            </div>
            {a.audience_name && a.audience_name !== a.artist && (
              <div className="text-[11px] text-muted-foreground truncate">{a.audience_name}</div>
            )}
            <div className="mt-1 font-mono text-xs text-muted-foreground break-all">{a.audience_id}</div>
            {isOpen && (
              <div className="mt-2 border-t border-border pt-2">
                <SourcePageCard page={a.page} subtype={a.subtype} />
              </div>
            )}
            <div className="mt-2 flex justify-end">
              <button
                onClick={(e) => { e.stopPropagation(); setPushTarget(a); }}
                disabled={!a.active}
                className="px-2 py-0.5 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
                title="Create a paused ad set on Meta targeting this audience"
              >Push to event…</button>
            </div>
          </div>
          );
        })}
        {visible.length === 0 && (
          <div className="text-center text-muted-foreground py-6 text-sm">No audiences match.</div>
        )}
      </div>

      <div className="border border-border rounded-md overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-2 py-2 w-6"></th>
              <SortableTh sortKey="artist"      label="Artist"      currentKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh sortKey="subtype"     label="Type"        currentKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh sortKey="size"        label="Size"        currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
              <SortableTh sortKey="audience_id" label="Audience ID" currentKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh sortKey="page"        label="Source page" currentKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortableTh sortKey="status"      label="Status"      currentKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <th className="px-3 py-2 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((a) => {
              const isOpen = expanded.has(a.id);
              return (
              <Fragment key={a.id}>
              <tr className="border-t border-border hover:bg-accent/30 cursor-pointer" onClick={() => toggleExpand(a.id)}>
                <td className="px-2 py-2 text-muted-foreground">{isOpen ? '▾' : '▸'}</td>
                <td className="px-3 py-2">
                  <div>{a.artist}</div>
                  {a.audience_name && a.audience_name !== a.artist && (
                    <div className="text-[11px] text-muted-foreground truncate max-w-xs">{a.audience_name}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{subtypeLabel(a.subtype)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground whitespace-nowrap">{fmtCount(a.approx_count_upper ?? a.approx_count_lower)}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{a.audience_id}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                  {a.page ? a.page.name : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2">
                  {a.active ? (
                    <span className="text-xs text-green-700 dark:text-green-300">active</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">inactive</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); setPushTarget(a); }}
                    disabled={!a.active}
                    className="px-2 py-0.5 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
                    title="Create a paused ad set on Meta targeting this audience"
                  >Push to event…</button>
                </td>
              </tr>
              {isOpen && (
                <tr className="bg-muted/20 border-t border-border">
                  <td colSpan={8} className="px-4 py-3"><SourcePageCard page={a.page} subtype={a.subtype} /></td>
                </tr>
              )}
              </Fragment>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No audiences match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pushTarget && (
        <PushToEventDialog
          tenantSlug={tenantSlug}
          audience={pushTarget}
          onClose={() => setPushTarget(null)}
        />
      )}

      {uploadOpen && (
        <UploadAudienceDialog
          tenantSlug={tenantSlug}
          audiences={data.audiences}
          onClose={() => setUploadOpen(false)}
          onUploaded={load}
        />
      )}

      <div className="mt-4 text-xs text-muted-foreground">
        Meta performance metrics live in the <span className="font-medium">Meta performance</span> tab, this page is just the audience registry.
      </div>
    </div>
  );
}

// Inline detail for an audience: the source Page/IG it's built from, or a note
// for non-page-based subtypes (website pixel / lookalike / CSV upload).
function SourcePageCard({ page, subtype }) {
  if (!page) {
    const why = subtype === 'WEBSITE' ? 'website pixel traffic'
      : subtype === 'LOOKALIKE' ? 'a lookalike of another audience'
      : subtype === 'CUSTOM' ? 'an uploaded customer list'
      : 'a non-page source';
    return <div className="text-xs text-muted-foreground">Not page-based, built from {why}.</div>;
  }
  return (
    <div className="flex items-center gap-3">
      {page.picture
        ? <img src={page.picture} alt="" referrerPolicy="no-referrer" className="w-10 h-10 rounded-full object-cover bg-muted flex-shrink-0" />
        : <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <a href={page.link || `https://facebook.com/${page.id}`} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">{page.name}</a>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${page.access === 'owned' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>{page.access === 'owned' ? 'Owned' : 'Shared'}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          {page.followers != null && <span>{page.followers.toLocaleString()} followers</span>}
          {page.instagram && <span> · <a href={`https://instagram.com/${page.instagram}`} target="_blank" rel="noreferrer" className="hover:underline">@{page.instagram}</a></span>}
          <span className="ml-1 font-mono text-[11px]">· {page.id}</span>
        </div>
      </div>
    </div>
  );
}

function SortableTh({ sortKey, label, currentKey, dir, onClick, align = 'left' }) {
  const active = sortKey === currentKey;
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`px-3 py-2 font-medium cursor-pointer hover:bg-accent/40 select-none text-${align}`}
    >
      {label}
      {active && <span className="ml-1 text-xs text-muted-foreground">{dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );
}

function PushToEventDialog({ tenantSlug, audience, onClose }) {
  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [budget, setBudget] = useState(1000);
  const [campaign, setCampaign] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    apiFetch(`/api/tenants/${tenantSlug}/events`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.error);
        const matches = d.events.filter((e) =>
          e.talent && e.talent.toLowerCase().includes(audience.artist.toLowerCase().split(' ')[0])
        );
        const list = matches.length > 0 ? matches : d.events;
        setEvents(list);
        if (matches.length > 0) setSelectedIds(new Set(matches.map((e) => e.id)));
        else if (list[0]) setSelectedIds(new Set([list[0].id]));
      })
      .catch(() => setEvents([]));
  }, [tenantSlug, audience.artist]);

  const toggle = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectAll = () => setSelectedIds(new Set(events.map((e) => e.id)));
  const selectNone = () => setSelectedIds(new Set());

  const submit = async () => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    setResults(null);
    const out = [];
    const body = { daily_budget_cents: Number(budget) };
    if (campaign.trim()) body.campaign_id = campaign.trim();
    for (const eventId of [...selectedIds]) {
      try {
        const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${eventId}/meta-push`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const d = await res.json();
        const ev = events.find((e) => e.id === eventId);
        out.push({ ok: res.ok, eventId, talent: ev?.talent, date: ev?.date, ...d });
      } catch (e) {
        out.push({ ok: false, eventId, error: e.message });
      }
    }
    setResults(out);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-md max-w-lg w-full p-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium">Push {audience.artist} to events</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="text-xs text-muted-foreground mb-3 font-mono">audience: {audience.audience_id}</div>

        <div className="space-y-3 text-sm">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-muted-foreground">Events ({selectedIds.size} selected)</label>
              <div className="text-xs flex gap-2">
                <button onClick={selectAll} className="text-primary hover:underline" type="button">All</button>
                <button onClick={selectNone} className="text-muted-foreground hover:underline" type="button">None</button>
              </div>
            </div>
            <div className="border border-input rounded-md max-h-48 overflow-y-auto">
              {events.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground text-center">No events</div>
              ) : (
                events.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-accent cursor-pointer border-b border-border last:border-b-0">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(e.id)}
                      onChange={() => toggle(e.id)}
                    />
                    <span className="flex-1">{e.talent}</span>
                    <span className="text-muted-foreground">{new Date(e.date).toLocaleDateString()}</span>
                  </label>
                ))
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              One ad set will be created per selected event.
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Daily budget (cents)</label>
            <input
              type="number"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="w-full px-2 py-1 border border-input rounded-md bg-background"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Campaign ID (optional; uses META_DEFAULT_CAMPAIGN_ID if blank)</label>
            <input
              type="text"
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              placeholder="123456789…"
              className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono text-xs"
            />
          </div>
        </div>

        {results && (
          <div className="text-xs mt-3 border border-border rounded-md overflow-hidden">
            <div className="px-2 py-1 bg-muted/40 font-medium">
              {results.filter((r) => r.ok).length} of {results.length} succeeded
              {results.filter((r) => !r.ok).length > 0 && (
                <span className="text-destructive ml-2">({results.filter((r) => !r.ok).length} failed)</span>
              )}
            </div>
            {results.map((r, i) => (
              <div key={i} className={`px-2 py-1 border-t border-border ${r.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-destructive'}`}>
                <div className="flex items-center gap-2">
                  <span>{r.ok ? '✓' : '✗'}</span>
                  <span className="flex-1">{r.talent} · {r.date && new Date(r.date).toLocaleDateString()}</span>
                  <span className="text-[10px] font-mono opacity-70">{r.ok ? `adset ${r.adset_id}` : r.error}</span>
                </div>
                {r.detail && <div className="text-[10px] opacity-70 ml-4">{r.detail}</div>}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1 text-sm border border-input rounded-md hover:bg-accent">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || selectedIds.size === 0}
            className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
          >{busy ? `Pushing… (${results ? results.length : 0}/${selectedIds.size})` : `Create ${selectedIds.size} ad set${selectedIds.size === 1 ? '' : 's'}`}</button>
        </div>
      </div>
    </div>
  );
}

// --- Upload custom (CRM) audience -------------------------------------------

// Minimal quoted-field CSV parser — enough for CRM exports; no dependency.
function parseCsv(text) {
  const rows = [];
  let row = [], field = '', inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.length > 1 || row[0] !== '') rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.length > 1 || row[0] !== '') rows.push(row);
  return rows;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneish = (s) => { const d = String(s || '').replace(/\D/g, ''); return d.length >= 10 && d.length <= 15; };

// CSV/pasted text → [{email, phone}]. Uses header names (email / phone-ish)
// when present, else a per-cell content heuristic. Rows with neither → skipped.
function extractContacts(text) {
  const rows = parseCsv(text);
  if (!rows.length) return { contacts: [], skipped: 0 };
  const head = rows[0].map((h) => String(h).trim().toLowerCase());
  const emailCol = head.findIndex((h) => /e-?mail/.test(h));
  const phoneCol = head.findIndex((h) => /phone|mobile|cell|^tel/.test(h));
  const hasHeader = emailCol >= 0 || phoneCol >= 0;
  const body = hasHeader ? rows.slice(1) : rows;
  const contacts = []; let skipped = 0;
  for (const r of body) {
    let email = hasHeader && emailCol >= 0 ? String(r[emailCol] || '').trim() : '';
    let phone = hasHeader && phoneCol >= 0 ? String(r[phoneCol] || '').trim() : '';
    if (!email && !phone) {
      for (const cell of r) {
        const v = String(cell || '').trim();
        if (!email && EMAIL_RE.test(v)) email = v;
        else if (!phone && !EMAIL_RE.test(v) && phoneish(v)) phone = v;
      }
    }
    if (email && !EMAIL_RE.test(email)) email = '';
    if (phone && !phoneish(phone)) phone = '';
    if (email || phone) contacts.push({ email, phone });
    else skipped++;
  }
  return { contacts, skipped };
}

// Create a new customer-file audience (or append to an existing one) from a
// CSV / pasted contact list. Contacts are hashed server-side before Meta ever
// sees them; Meta matches over ~an hour, so size shows on a later sync.
function UploadAudienceDialog({ tenantSlug, audiences, onClose, onUploaded }) {
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [name, setName] = useState('');
  const [targetId, setTargetId] = useState('');
  const [rawText, setRawText] = useState('');
  const [file, setFile] = useState(null);      // File object (large path)
  const [fileName, setFileName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);        // background job progress

  const customAudiences = audiences.filter((a) => a.subtype === 'CUSTOM' && a.active);
  // Preview only for pasted text (a huge file isn't parsed in the browser).
  const { contacts, skipped } = useMemo(() => (file ? { contacts: [], skipped: 0 } : extractContacts(rawText)), [rawText, file]);
  const emails = contacts.filter((c) => c.email).length;
  const phones = contacts.filter((c) => c.phone).length;
  const hasInput = file ? file.size > 0 : contacts.length > 0;
  const ready = hasInput && (mode === 'new' ? name.trim().length > 0 : !!targetId);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setFile(f);          // always take the file path for uploads (handles any size)
    setRawText('');
    setErr(null);
  };

  // Poll a background job until it finishes.
  const pollJob = (jobId) => {
    const tick = async () => {
      try {
        const r = await apiFetch(`/api/tenants/${tenantSlug}/meta-audiences/uploads`);
        const d = await r.json();
        const j = (d.uploads || []).find((u) => u.id === jobId);
        if (!j) return;
        setJob(j);
        if (j.status === 'done' || j.status === 'error') {
          setBusy(false);
          if (j.status === 'error') setErr(j.error || 'upload failed');
          else setResult({ received: j.uploaded, invalid: j.invalid, audience_id: j.audience_id, created: mode === 'new', background: true });
          onUploaded?.();
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(tick, 2000);
    };
    setTimeout(tick, 1500);
  };

  const submit = async () => {
    setBusy(true); setErr(null); setResult(null); setJob(null);
    try {
      if (file) {
        // Large path: stream the raw file to the backend; it parses/hashes/
        // uploads in the background and we poll for progress.
        const qs = new URLSearchParams(mode === 'new' ? { name: name.trim() } : { audience_id: targetId });
        qs.set('file_name', file.name);
        const r = await apiFetch(`/api/tenants/${tenantSlug}/meta-audiences/upload-file?${qs}`, {
          method: 'POST', headers: { 'Content-Type': 'text/csv' }, body: file,
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error === 'read_only_mode' ? 'Writes are disabled (READ_ONLY_MODE).' : (d.detail || d.error || `failed (${r.status})`));
        setJob({ status: 'running', uploaded: 0 });
        pollJob(d.job_id);
        return; // stays busy; poller resolves it
      }
      // Small path: pasted contacts, synchronous.
      const body = { contacts };
      if (mode === 'new') body.name = name.trim();
      else body.audience_id = targetId;
      const r = await apiFetch(`/api/tenants/${tenantSlug}/meta-audiences/upload`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error === 'read_only_mode' ? 'Writes are disabled (READ_ONLY_MODE).' : (d.upstream || d.detail || d.error || `failed (${r.status})`));
      setResult(d);
      onUploaded?.();
      setBusy(false);
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-md max-w-lg w-full p-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium">Upload custom audience</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>

        {result ? (
          <div className="space-y-3 text-sm">
            <div className="text-emerald-700 dark:text-emerald-400">
              ✓ Uploaded {result.received?.toLocaleString?.() ?? result.received} contact{result.received === 1 ? '' : 's'}
              {result.invalid > 0 && <span className="text-muted-foreground"> ({result.invalid} invalid)</span>}
              {result.created && <span> to new audience</span>}
            </div>
            <div className="text-xs text-muted-foreground font-mono">audience: {result.audience_id}</div>
            <div className="text-xs text-muted-foreground">
              Meta matches contacts over roughly the next hour; the audience size updates on a later sync.
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90">Done</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex gap-4 text-xs">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> Create new audience
              </label>
              <label className={`flex items-center gap-1.5 ${customAudiences.length ? 'cursor-pointer' : 'opacity-50'}`}>
                <input type="radio" checked={mode === 'existing'} disabled={!customAudiences.length} onChange={() => setMode('existing')} /> Add to existing
              </label>
            </div>

            {mode === 'new' ? (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Audience name</label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. CRM, VIP guests 2026"
                  className="w-full px-2 py-1 border border-input rounded-md bg-background"
                />
              </div>
            ) : (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Existing customer-file audience</label>
                <select
                  value={targetId} onChange={(e) => setTargetId(e.target.value)}
                  className="w-full px-2 py-1 border border-input rounded-md bg-background"
                >
                  <option value="">Select…</option>
                  {customAudiences.map((a) => (
                    <option key={a.audience_id} value={a.audience_id}>
                      {a.audience_name || a.artist} ({fmtCount(a.approx_count_upper ?? a.approx_count_lower)})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Contacts: upload a CSV file (any size) or paste a list</label>
              <div className="flex items-center gap-2 mb-2">
                <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="text-xs" />
                {file && (
                  <button type="button" onClick={() => { setFile(null); setFileName(null); }} className="text-[11px] text-muted-foreground hover:text-destructive">clear</button>
                )}
              </div>
              {file && <div className="text-[11px] text-muted-foreground mb-2">{fileName} · {(file.size / 1024 / 1024).toFixed(2)} MB, parsed &amp; uploaded on the server.</div>}
              {!file && (
                <textarea
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  rows={6}
                  placeholder={'email,phone\njane@example.com,305-555-0100\njoe@example.com\n+1 786 555 0101'}
                  className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono text-[11px]"
                />
              )}
              <div className="text-[11px] text-muted-foreground mt-1">
                {file
                  ? 'Large file: streamed to the server, parsed line-by-line, hashed, and uploaded to Meta in the background. You can leave this open to watch progress.'
                  : contacts.length > 0
                    ? <>Ready: <span className="font-medium text-foreground">{contacts.length.toLocaleString()} contacts</span> · {emails.toLocaleString()} emails · {phones.toLocaleString()} phones{skipped > 0 && <> · {skipped.toLocaleString()} rows skipped (no email/phone)</>}</>
                    : 'Columns are auto-detected by header (email / phone) or content. Emails and phones are hashed server-side before being sent to Meta.'}
              </div>
            </div>

            {job && job.status === 'running' && (
              <div className="text-xs text-muted-foreground border border-border rounded-md px-2 py-1.5">
                Uploading in background… <span className="tabular-nums text-foreground font-medium">{(job.uploaded || 0).toLocaleString()}</span> contacts sent so far{job.total_rows ? ` of ${job.total_rows.toLocaleString()} rows` : ''}.
              </div>
            )}

            {err && <div className="text-xs text-destructive break-words">{err}</div>}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} disabled={busy} className="px-3 py-1 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50">{busy ? 'Close (keeps running)' : 'Cancel'}</button>
              <button
                onClick={submit}
                disabled={busy || !ready}
                className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
              >{busy ? 'Uploading…' : file ? 'Upload file' : `Upload ${contacts.length > 0 ? contacts.length.toLocaleString() : ''} contact${contacts.length === 1 ? '' : 's'}`}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
